/**
 * Maps overview failed-sample clicks to QA/QC unified test screen + sub-tab.
 */

import {
  mergeOverviewTabIntoQuery,
  overviewTabFromNavigationTarget,
  parseQaqcOverviewTabFromQuery,
} from "./qaqcOverviewTab.js";

function normalizeCriterionLabel(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[’']/g, "'")
    .trim();
}

function matchesAny(label, patterns) {
  const n = normalizeCriterionLabel(label);
  if (!n) return false;
  return patterns.some((p) => {
    const pat = normalizeCriterionLabel(p);
    return pat && n.includes(pat);
  });
}

/** DL / separator material-balance distribution plots and matrix checks (mirror DL stepper). */
const MATERIAL_BALANCE_NAV_PATTERNS = [
  "total material balance",
  "total material balance error",
  "forward backward",
  "forward/backward",
  "forwardbackward",
  "forward and backward moles",
  "sto density",
  "sto densty",
  "stodensityerror",
  "total gor error",
  "total gor",
  "totalgorerror",
  "reported and calculated total gor",
  "reported and calculated sto",
  "material balance error",
  "rs error",
  "forwardbackwardmoles",
];

function isMaterialBalanceNavigation(label) {
  return matchesAny(label, MATERIAL_BALANCE_NAV_PATTERNS);
}

/**
 * @typedef {Object} QaqcNavigationTarget
 * @property {'cce'|'dl'|'separator'} testKind
 * @property {string|null} dlScreen
 * @property {'table'|'plots'|'tables'|null} tab
 */

function dlLikeNavKind(testKind) {
  return testKind === "separator" ? "separator" : "dl";
}

/**
 * @param {{ criterionName?: string, source?: string, testKind?: 'cce'|'dl'|'separator' }} ctx
 * @returns {QaqcNavigationTarget}
 */
export function resolveQaqcFailedSampleNavigation(ctx = {}) {
  const label = ctx.criterionName || "";
  const source = ctx.source || "";
  const kind =
    ctx.testKind === "separator"
      ? "separator"
      : ctx.testKind === "dl"
        ? "dl"
        : "cce";
  const dlKind = dlLikeNavKind(kind);

  if (source === "dlConsistencyCheck") {
    return { testKind: dlKind, dlScreen: "initialScreening", tab: "table" };
  }
  if (source === "dlEquilibrium") {
    return { testKind: dlKind, dlScreen: "consistencyAnalysis", tab: null };
  }
  if (source === "dlValidationMatrix") {
    if (matchesAny(label, ["overall feed deviation"])) {
      return { testKind: dlKind, dlScreen: "feedValidation", tab: null };
    }
    if (matchesAny(label, ["equilibrium plot linearity"])) {
      return { testKind: dlKind, dlScreen: "consistencyAnalysis", tab: null };
    }
    if (matchesAny(label, ["dl properties consistency"])) {
      return { testKind: dlKind, dlScreen: "initialScreening", tab: "table" };
    }
    if (isMaterialBalanceNavigation(label)) {
      return { testKind: dlKind, dlScreen: "totalMaterialBalance", tab: null };
    }
    return { testKind: dlKind, dlScreen: "initialScreening", tab: "plots" };
  }
  if (source === "dlDistribution") {
    if (matchesAny(label, ["overall feed deviation"])) {
      return { testKind: dlKind, dlScreen: "feedValidation", tab: null };
    }
    if (isMaterialBalanceNavigation(label)) {
      return { testKind: dlKind, dlScreen: "totalMaterialBalance", tab: null };
    }
    return { testKind: dlKind, dlScreen: "initialScreening", tab: "plots" };
  }

  if (source === "cceDistribution") {
    if (
      matchesAny(label, [
        "relative volume monotonicity violation",
        "vi/vtot monotonicity violation",
        "oil density at psat",
        "oil compressibility at psat",
        "oil compressibility at pres",
        "gas z factor at psat",
        "gas density at psat",
      ])
    ) {
      return { testKind: "cce", dlScreen: null, tab: "table" };
    }
    if (matchesAny(label, ["y-function linearity", "r^2 values"])) {
      return { testKind: "cce", dlScreen: null, tab: "plots" };
    }
    return { testKind: "cce", dlScreen: null, tab: "table" };
  }

  if (kind === "dl" || kind === "separator") {
    return { testKind: kind, dlScreen: "initialScreening", tab: "plots" };
  }

  const ccePlotPatterns = [
    "vrel consistency",
    "vl/vtot consistency",
    "vl/vsat consistency",
    "y-function linear",
    "smooth oil compressibility",
    "densities monotonically decrease",
    "gas z factor monotonically increase",
  ];
  if (matchesAny(label, ccePlotPatterns)) {
    return { testKind: "cce", dlScreen: null, tab: "plots" };
  }

  if (matchesAny(label, ["y-function"]) && !matchesAny(label, ["linear"])) {
    return { testKind: "cce", dlScreen: null, tab: "table" };
  }

  return { testKind: "cce", dlScreen: null, tab: "table" };
}

export function pickTestFromSamplePayload(sample, testKind) {
  if (!sample) return { testId: null, routeTestType: null };
  const preferSeparator = testKind === "separator";
  const preferDl = testKind === "dl";
  const sep = Array.isArray(sample.separatortests) ? sample.separatortests : [];
  const dl = Array.isArray(sample.dltests) ? sample.dltests : [];
  const cce = Array.isArray(sample.ccetests) ? sample.ccetests : [];

  const first = (arr) => {
    if (!arr.length) return null;
    const t = arr[0];
    const testId = t.ID || t._id || t.id;
    return testId != null ? String(testId) : null;
  };

  if (preferSeparator) {
    const sid = first(sep);
    if (sid) return { testId: sid, routeTestType: "separatortest" };
    const id = first(dl);
    if (id) return { testId: id, routeTestType: "dltest" };
    const cid = first(cce);
    if (cid) return { testId: cid, routeTestType: "ccetest" };
  } else if (preferDl) {
    const id = first(dl);
    if (id) return { testId: id, routeTestType: "dltest" };
    const cid = first(cce);
    if (cid) return { testId: cid, routeTestType: "ccetest" };
  } else {
    const cid = first(cce);
    if (cid) return { testId: cid, routeTestType: "ccetest" };
    const id = first(dl);
    if (id) return { testId: id, routeTestType: "dltest" };
  }
  return { testId: null, routeTestType: null };
}

/**
 * @param {import('vue-router').Router} router
 * @param {{
 *   tenantId: string,
 *   mongoSampleId: string,
 *   testId: string,
 *   routeTestType: string,
 *   well?: string,
 *   fluidSampleID?: string,
 *   navigationTarget: QaqcNavigationTarget,
 *   listQuery?: Record<string, unknown>,
 * }} opts
 */
export function pushQaqcOverviewFailedSampleRoute(router, opts) {
  const {
    tenantId,
    mongoSampleId,
    testId,
    routeTestType,
    well,
    fluidSampleID,
    navigationTarget,
    listQuery = {},
  } = opts;

  if (!tenantId || !mongoSampleId || !testId || !routeTestType) return;

  const overviewTab =
    parseQaqcOverviewTabFromQuery(listQuery) ||
    overviewTabFromNavigationTarget(navigationTarget, listQuery);
  const query = mergeOverviewTabIntoQuery({ ...listQuery }, overviewTab);
  if (well != null && String(well).trim() !== "") {
    query.well = String(well);
  }
  if (fluidSampleID != null && String(fluidSampleID).trim() !== "") {
    query.fluidSampleID = String(fluidSampleID);
  }
  if (navigationTarget?.dlScreen) {
    query.qaqcScreen = navigationTarget.dlScreen;
  }
  if (navigationTarget?.tab) {
    query.qaqcTab = navigationTarget.tab;
  }

  const path = `/${tenantId}/qaqc/test/${mongoSampleId}/${routeTestType}/${testId}`;
  router.push({ path, query });
}
